import React, { useState, useEffect } from 'react';
import {
  BarChart3, PieChart as PieChartIcon, TrendingUp, FileText, Clock,
  Calendar, RefreshCw, Loader2, DollarSign, AlertTriangle, Scale,
  ArrowUp, ArrowDown, Activity, FolderOpen, Gavel
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Area, AreaChart
} from 'recharts';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import EstadisticasAnualTab from '../components/EstadisticasAnualTab';

const API = process.env.REACT_APP_BACKEND_URL;

const EstadisticasExpedientes = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const res = await axios.get(`${API}/api/estadisticas/expedientes`);
      setStats(res.data);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
      toast({ title: 'Error', description: 'No se pudieron cargar las estadísticas', variant: 'destructive' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-10">
        <p className="text-gray-500">No se pudieron cargar las estadísticas</p>
        <Button onClick={() => loadStats()} className="mt-4">Reintentar</Button>
      </div>
    );
  }

  const { resumen, por_estado, por_rama, por_fase, por_tipo_procedimiento, evolucion_mensual, valor_economico, actuaciones_recientes, expedientes_sin_actividad } = stats;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-blue-600" />
            Estadísticas de Expedientes
          </h1>
          <p className="text-gray-600 mt-1">Análisis detallado de todos los expedientes del despacho</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.location.href = '/expedientes/pago-tasas'}>
            Pago de Tasas
          </Button>
          <Button variant="outline" onClick={() => loadStats(true)} disabled={refreshing}>
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Actualizar
          </Button>
        </div>
      </div>

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="grid w-full md:w-auto grid-cols-2 mb-4" data-testid="estadisticas-tabs">
          <TabsTrigger value="general" data-testid="tab-general">Vista general</TabsTrigger>
          <TabsTrigger value="anual" data-testid="tab-anual">Análisis por años</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-6">
      {/* KPIs Principales */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-xs">Total</p>
                <p className="text-2xl font-bold">{resumen.total}</p>
              </div>
              <FolderOpen className="w-8 h-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-xs">Activos</p>
                <p className="text-2xl font-bold">{resumen.activos}</p>
              </div>
              <Activity className="w-8 h-8 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-xs">Suspendidos</p>
                <p className="text-2xl font-bold">{resumen.suspendidos}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-gray-500 to-gray-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-100 text-xs">Cerrados</p>
                <p className="text-2xl font-bold">{resumen.cerrados}</p>
              </div>
              <FileText className="w-8 h-8 text-gray-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-xs">Este Mes</p>
                <p className="text-2xl font-bold">{resumen.creados_este_mes}</p>
              </div>
              <Calendar className="w-8 h-8 text-purple-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-cyan-100 text-xs">Este Año</p>
                <p className="text-2xl font-bold">{resumen.creados_este_anio}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-cyan-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100 text-xs">Tasa Cierre</p>
                <p className="text-2xl font-bold">{resumen.tasa_cierre}%</p>
              </div>
              <Scale className="w-8 h-8 text-indigo-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Valor Económico */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            Valor Económico de Expedientes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-600">Valor Total</p>
              <p className="text-2xl font-bold text-green-700">{formatCurrency(valor_economico.total)}</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600">Valor Activos</p>
              <p className="text-2xl font-bold text-blue-700">{formatCurrency(valor_economico.activos)}</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-gray-600">Promedio</p>
              <p className="text-2xl font-bold text-purple-700">{formatCurrency(valor_economico.promedio)}</p>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-lg">
              <p className="text-sm text-gray-600">Máximo</p>
              <p className="text-2xl font-bold text-amber-700">{formatCurrency(valor_economico.maximo)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gráficos principales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Por Estado */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChartIcon className="w-5 h-5" />
              Distribución por Estado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={por_estado.filter(e => e.valor > 0)}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="valor"
                  label={({ nombre, valor }) => `${nombre}: ${valor}`}
                >
                  {por_estado.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Por Rama */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gavel className="w-5 h-5" />
              Expedientes por Rama Jurídica
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={por_rama} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="nombre" type="category" width={120} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="valor" fill="#3B82F6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Evolución Mensual */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Evolución Últimos 12 Meses
          </CardTitle>
          <CardDescription>Expedientes creados vs cerrados por mes</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={evolucion_mensual}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" tick={{ fontSize: 10 }} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="creados" name="Creados" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.3} />
              <Area type="monotone" dataKey="cerrados" name="Cerrados" stroke="#10B981" fill="#10B981" fillOpacity={0.3} />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Fila inferior */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Por Fase */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Por Fase Procesal</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {por_fase.slice(0, 8).map((fase, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 truncate flex-1">{fase.nombre}</span>
                  <Badge variant="secondary">{fase.valor}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Por Tipo Procedimiento */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Por Tipo de Procedimiento</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {por_tipo_procedimiento.slice(0, 8).map((tipo, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 truncate flex-1" title={tipo.nombre}>{tipo.nombre}</span>
                  <Badge variant="secondary">{tipo.valor}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Alertas */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Expedientes Sin Actividad (+30 días)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {expedientes_sin_actividad.length > 0 ? (
              <div className="space-y-2">
                {expedientes_sin_actividad.slice(0, 5).map((exp, idx) => (
                  <div key={idx} className="p-2 bg-amber-50 rounded text-sm">
                    <p className="font-medium">{exp.numero}</p>
                    <p className="text-xs text-gray-500 truncate">{exp.titulo}</p>
                  </div>
                ))}
                {expedientes_sin_actividad.length > 5 && (
                  <p className="text-xs text-gray-500 text-center">
                    +{expedientes_sin_actividad.length - 5} más
                  </p>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-sm text-center py-4">
                ✅ Todos los expedientes tienen actividad reciente
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Actuaciones Recientes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Últimas Actuaciones (7 días)</CardTitle>
        </CardHeader>
        <CardContent>
          {actuaciones_recientes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
              {actuaciones_recientes.map((act, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-sm truncate">{act.titulo}</p>
                  <p className="text-xs text-blue-600">{act.expediente}</p>
                  <p className="text-xs text-gray-400">{act.fecha ? new Date(act.fecha).toLocaleDateString('es-ES') : '-'}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm text-center py-4">No hay actuaciones recientes</p>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="anual">
          <EstadisticasAnualTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EstadisticasExpedientes;
